import { useState } from 'react';

import { Button, FieldLabel, InputSwitch, Password, SelectButton, showSnackbar } from 'fluxo-ui';
import { AlertIcon, BrightSunIcon, ShieldIcon } from 'fluxo-ui/icons';

import userService from '../services/user.service';
import { useUIStore } from '../store/uiStore';

interface SectionCardProps {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
    children: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({ icon: Icon, title, description, children }) => (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="flex items-center gap-3 p-5 border-b border-slate-200 dark:border-slate-700">
            <div className="w-10 h-10 rounded-lg bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5" />
            </div>
            <div>
                <h2 className="font-semibold text-slate-900 dark:text-white">{title}</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">{description}</p>
            </div>
        </div>
        <div className="p-6">{children}</div>
    </div>
);

const SettingsPage: React.FC = () => {
    const { theme, setTheme } = useUIStore();

    // Notification settings
    const [emailNotifications, setEmailNotifications] = useState(true);
    const [examReminders, setExamReminders] = useState(true);
    const [resultNotifications, setResultNotifications] = useState(true);

    // Password change
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordErrors, setPasswordErrors] = useState<{ currentPassword?: string; newPassword?: string; confirmPassword?: string }>({});
    const [isSavingPassword, setIsSavingPassword] = useState(false);

    const handleSaveNotifications = () => {
        showSnackbar('Notification preferences saved', 'Success', { type: 'success' });
    };

    const handleChangePassword = async () => {
        const errors: { currentPassword?: string; newPassword?: string; confirmPassword?: string } = {};

        if (!currentPassword) {
            errors.currentPassword = 'Current password is required';
        }

        if (!newPassword) {
            errors.newPassword = 'New password is required';
        } else if (newPassword.length < 8) {
            errors.newPassword = 'Password must be at least 8 characters';
        }

        if (!confirmPassword) {
            errors.confirmPassword = 'Please confirm your new password';
        } else if (newPassword !== confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        if (Object.keys(errors).length > 0) {
            setPasswordErrors(errors);
            return;
        }

        setIsSavingPassword(true);
        try {
            await userService.updatePassword({
                currentPassword,
                newPassword,
                confirmPassword,
            });
            showSnackbar('Password changed successfully', 'Success', { type: 'success' });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setPasswordErrors({});
        } catch (err: any) {
            showSnackbar(err.message || 'Failed to change password', 'Error', { type: 'error' });
        } finally {
            setIsSavingPassword(false);
        }
    };

    const notificationRows = [
        {
            title: 'Email Notifications',
            description: 'Receive email notifications for important updates',
            checked: emailNotifications,
            onChange: setEmailNotifications,
        },
        {
            title: 'Exam Reminders',
            description: 'Get reminded about upcoming exams',
            checked: examReminders,
            onChange: setExamReminders,
        },
        {
            title: 'Result Notifications',
            description: 'Notify when exam results are available',
            checked: resultNotifications,
            onChange: setResultNotifications,
        },
    ];

    return (
        <div className="max-w-4xl">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-1">Settings</h1>
                <p className="text-slate-600 dark:text-slate-400">Manage your account preferences and security</p>
            </div>

            <div className="space-y-6">
                {/* Appearance */}
                <SectionCard icon={BrightSunIcon} title="Appearance" description="Theme, language and regional preferences">
                    <div className="space-y-6">
                        <FieldLabel label="Theme">
                            <SelectButton
                                items={[
                                    { label: 'Light', value: 'light' },
                                    { label: 'Dark', value: 'dark' },
                                ]}
                                value={theme}
                                onChange={(e) => setTheme(e.value as 'light' | 'dark')}
                            />
                        </FieldLabel>

                        <div className="pt-6 border-t border-slate-200 dark:border-slate-700">
                            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Language &amp; Region</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <FieldLabel label="Language">
                                    <SelectButton
                                        items={[
                                            { label: 'English', value: 'en' },
                                            { label: 'Spanish', value: 'es' },
                                        ]}
                                        value="en"
                                        onChange={() => {}}
                                        direction="vertical"
                                    />
                                </FieldLabel>
                                <FieldLabel label="Timezone">
                                    <SelectButton
                                        items={[
                                            { label: 'UTC', value: 'UTC' },
                                            { label: 'EST', value: 'EST' },
                                            { label: 'PST', value: 'PST' },
                                        ]}
                                        value="UTC"
                                        onChange={() => {}}
                                        direction="vertical"
                                    />
                                </FieldLabel>
                            </div>
                        </div>
                    </div>
                </SectionCard>

                {/* Notifications */}
                <SectionCard icon={AlertIcon} title="Notifications" description="Choose which updates reach your inbox">
                    <div className="divide-y divide-slate-100 dark:divide-slate-700/60">
                        {notificationRows.map((row, index) => (
                            <div key={index} className="flex items-center justify-between gap-4 py-3.5 first:pt-0">
                                <div>
                                    <div className="text-sm font-medium text-slate-900 dark:text-white">{row.title}</div>
                                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{row.description}</div>
                                </div>
                                <InputSwitch checked={row.checked} onChange={(e) => row.onChange(e.value)} />
                            </div>
                        ))}
                    </div>

                    <div className="pt-5">
                        <Button label="Save Preferences" variant="primary" onClick={handleSaveNotifications} />
                    </div>
                </SectionCard>

                {/* Security */}
                <SectionCard icon={ShieldIcon} title="Security" description="Password and account protection">
                    <div className="space-y-4 max-w-md">
                        <FieldLabel label="Current Password" required error={passwordErrors.currentPassword}>
                            <Password
                                value={currentPassword}
                                onChange={(e) => {
                                    setCurrentPassword(e.value);
                                    if (passwordErrors.currentPassword) {
                                        setPasswordErrors({ ...passwordErrors, currentPassword: undefined });
                                    }
                                }}
                                placeholder="Enter current password"
                                disabled={isSavingPassword}
                            />
                        </FieldLabel>

                        <FieldLabel label="New Password" required error={passwordErrors.newPassword}>
                            <Password
                                value={newPassword}
                                onChange={(e) => {
                                    setNewPassword(e.value);
                                    if (passwordErrors.newPassword) {
                                        setPasswordErrors({ ...passwordErrors, newPassword: undefined });
                                    }
                                }}
                                placeholder="Enter new password"
                                autoComplete="new-password"
                                disabled={isSavingPassword}
                            />
                        </FieldLabel>

                        <FieldLabel label="Confirm New Password" required error={passwordErrors.confirmPassword}>
                            <Password
                                value={confirmPassword}
                                onChange={(e) => {
                                    setConfirmPassword(e.value);
                                    if (passwordErrors.confirmPassword) {
                                        setPasswordErrors({ ...passwordErrors, confirmPassword: undefined });
                                    }
                                }}
                                placeholder="Confirm new password"
                                autoComplete="new-password"
                                disabled={isSavingPassword}
                            />
                        </FieldLabel>

                        <div className="pt-2">
                            <Button
                                label="Change Password"
                                variant="primary"
                                onClick={handleChangePassword}
                                isLoading={isSavingPassword}
                                disabled={isSavingPassword}
                            />
                        </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">Two-Factor Authentication</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Add an extra layer of security to your account</p>
                        <Button label="Enable 2FA" variant="default" layout="outlined" disabled />
                    </div>
                </SectionCard>
            </div>
        </div>
    );
};

export default SettingsPage;
